var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import * as classNames from 'classnames';
import * as moment from 'moment';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { isEmail } from 'validator';
import { Alert, Button, ButtonContainer, CircularProgress, Modal, ModalFooter, Spacer, Tooltip, Typography } from '@emburse/ui-core';
import { init, reset, submit } from '@Actions/cardsGatewayIssuingModalActions';
import { CardIssuingApiService } from '@ClientApi/cardIssuing';
import { CardRequestAPI } from '@ClientApi/cardRequestApi';
import { PendoEvents, PendoService } from '@ClientApi/pendo';
import { CARD_ISSUING_ERRORS, INSTITUTION_TYPE } from '@Shared/constants';
import { ADMIN_ROLE, MANAGER_ROLE } from '@Shared/constants/user_profile_constants';
import { parseCardsGatewayError } from '@Shared/helpers/cardsGatewayErrorFormatter';
import { canManagerIssueRequestedCard } from '@Shared/helpers/validators';
import { AccountCapabilitiesCardAllowanceIntervalEnum, AccountConnectionFeedBackendEnum, CardType, ShippingOptionServiceEnum } from '@Shared/interfaces/CardsGatewayInterfaces';
import { isManagerFlagCheck } from '../../../utils/userProfileRoleHelper';
import { AddressForm } from './Forms/AddressForm';
import { CardTypeForm } from './Forms/CardTypeForm';
import { ExpenseFieldsForm } from './Forms/ExpenseFieldsForm';
import { IssuingDetailsForm } from './Forms/IssuingDetailsForm';
import { ShippingMethodForm } from './Forms/ShippingMethodForm';
import { getAmexErrorsFromAmexGatewayValidationError } from './error_utils';
import * as style from './CardsGatewayIssuingModal.styl';
function isMultiSelectAllowed(state, props) {
    if (state.cardType === CardType.Physical || props.cardRequest) {
        return false;
    }
    if (props.linkedAccountId) {
        if (props.issuingMode === 'duplicate') {
            return true;
        }
        if (props.issuingMode === 'new') {
            return true;
        }
        return false;
    }
    return true;
}
export class CardsGatewayIssuingModal extends React.Component {
    constructor(props) {
        var _a, _b, _c;
        super(props);
        this.isVirtualCardForm = () => CardsGatewayIssuingModal.shouldBeVirtualCardForm(this.props.capabilities);
        this.isInEditMode = () => {
            if (typeof this.props.issuingMode === 'undefined') {
                return Boolean(this.props.linkedAccountId);
            }
            return this.props.issuingMode === 'edit';
        };
        this.cardCapabilities = () => { var _a, _b; return (this.isVirtualCardForm() ? (_a = this.props.capabilities) === null || _a === void 0 ? void 0 : _a.virtualCard : (_b = this.props.capabilities) === null || _b === void 0 ? void 0 : _b.card); };
        this._initializeShippingOptions = (stateParams, program) => {
            const isEligibleForExpeditedShipping = stateParams.cardType === CardType.Physical &&
                ['emburse', 'gringotts'].some((institution) => (program === null || program === void 0 ? void 0 : program.institution_type) === institution); // expedited shipping is only available for emburse cards and gringotts
            let groupedShippingOptions = {
                Standard: [],
                Express: [],
                Priority: []
            };
            if (isEligibleForExpeditedShipping) {
                CardIssuingApiService.getShippingOptions(this.props.parentLinkedAccountId)
                    .then((shippingOptions) => {
                    // Only supports US, non-international and individual shipping options
                    const filtertedShippingOptions = shippingOptions.filter((option) => option.originCountry === 'us' &&
                        option.destinationWithinOrigin &&
                        [ShippingOptionServiceEnum.Standard, ShippingOptionServiceEnum.Express].some((service) => service === option.service));
                    // groupd shippingOptions by service
                    groupedShippingOptions = filtertedShippingOptions.reduce((acc, option) => {
                        if (!acc[option.service]) {
                            acc[option.service] = [];
                        }
                        acc[option.service].push(option);
                        return acc;
                    }, {});
                    this.setState({
                        shippingService: 'Standard',
                        isEligibleForExpeditedShipping: isEligibleForExpeditedShipping,
                        shippingOptions: groupedShippingOptions,
                        showShippingMethodForm: Boolean((groupedShippingOptions === null || groupedShippingOptions === void 0 ? void 0 : groupedShippingOptions.Express) &&
                            (groupedShippingOptions === null || groupedShippingOptions === void 0 ? void 0 : groupedShippingOptions.Express.length) > 0 &&
                            (groupedShippingOptions === null || groupedShippingOptions === void 0 ? void 0 : groupedShippingOptions.Standard) &&
                            (groupedShippingOptions === null || groupedShippingOptions === void 0 ? void 0 : groupedShippingOptions.Standard.length) > 0)
                    });
                })
                    .catch(function () {
                    // fail silently
                });
            }
        };
        this.onChange = (updatedState) => {
            var _a;
            let hasUserChanged = updatedState.assigneeUserIds !== this.state.assigneeUserIds ||
                updatedState.assigneeUserId !== this.state.assigneeUserId ||
                updatedState.assigneeEmail !== this.state.assigneeEmail;
            if (updatedState.cardType === CardType.Physical) {
                updatedState.enableMultiSelect = false;
                updatedState.assigneeUserIds = [];
                hasUserChanged = true;
            }
            if (updatedState.cardType === CardType.Ghost || updatedState.cardType === CardType.Virtual) {
                if (isMultiSelectAllowed(this.state, this.props)) {
                    updatedState.enableMultiSelect = true;
                    hasUserChanged = true;
                }
            }
            if (this.state.enableMultiSelect && (updatedState.assigneeUserId || updatedState.assigneeEmail)) {
                const assigneeUserIds = this.state.assigneeUserIds || [];
                const existingSelection = assigneeUserIds === null || assigneeUserIds === void 0 ? void 0 : assigneeUserIds.find((x) => x === updatedState.assigneeEmail || x === updatedState.assigneeUserId);
                if (existingSelection) {
                    updatedState.assigneeUserIds = assigneeUserIds.filter((x) => x !== existingSelection);
                }
                else {
                    assigneeUserIds.push(updatedState.assigneeUserId || updatedState.assigneeEmail);
                    updatedState.assigneeUserIds = assigneeUserIds;
                }
            }
            this._initializeShippingOptions(updatedState, (_a = this.props.cardPrograms) === null || _a === void 0 ? void 0 : _a.find(({ id }) => id === this.props.parentLinkedAccountId));
            this.setState(Object.assign({ needsUserUpdate: hasUserChanged ? false : this.state.needsUserUpdate }, updatedState));
        };
        this.onClose = () => {
            this.props.reset();
            this.props.closeModal();
        };
        this.onDeny = () => {
            const carRequest = this.props.cardRequest;
            const onDeny = this.props.onDeny;
            if (carRequest && onDeny) {
                onDeny(carRequest);
            }
        };
        this.approveCardRequest = () => {
            const { onApprove } = this.props;
            if (!onApprove) {
                throw new Error('onApprove is not defined');
            }
            if (!this.props.cardRequest) {
                throw new Error('card request is undefiend');
            }
            const updatedRequest = this.getFieldsForForwardingRequest();
            onApprove(updatedRequest, true);
            this.onClose();
        };
        this.submitCard = () => {
            const errorMessage = this.validateFields();
            this.setState({ errorMessage }); // Will reset an old errorMessage if there is no error.
            if (errorMessage) {
                return; // Don't submit on validation error
            }
            const { parentLinkedAccountId, companyId, issuingDetailsOriginalValues, onSubmit } = this.props;
            let linkedAccountId = this.props.linkedAccountId;
            if (!this.isInEditMode()) {
                linkedAccountId = undefined; // unset for duplicating a card
            }
            const { assigneeUserIds, assigneeUserId, assigneeEmail, parentCardId, cardName, allowanceId, allowanceDailyLimit, allowanceMerchantCategories, allowanceMerchantGroupCategories, allowanceVendor, allowanceAmount, allowanceInterval, allowanceStartDate, allowanceEndDate, allowanceTransactionLimit, cardType, addressRecipient, addressStreet, addressCity, addressState, addressZipCode, addressPhoneNumber, selectedCategoryId, selectedExpenseTagsByBucketId, textExpenseTagsByBucketId, cardRequestId, cardRequestVersion, firstName, lastName, shippingService, shippingRequireSignature, cardTemplateId } = this.state;
            this.props.submit(parentLinkedAccountId, {
                assigneeUserIds,
                assigneeUserId,
                assigneeEmail,
                parentCardId,
                cardName,
                allowanceId,
                allowanceDailyLimit,
                allowanceMerchantCategories,
                allowanceMerchantGroupCategories,
                allowanceVendor,
                allowanceAmount,
                allowanceInterval,
                allowanceStartDate,
                allowanceEndDate,
                allowanceTransactionLimit,
                cardType,
                addressRecipient,
                addressStreet,
                addressCity,
                addressState,
                addressZipCode,
                addressPhoneNumber,
                selectedCategoryId: cardType === CardType.Physical || this.moreThanOneUserSelected ? null : selectedCategoryId,
                selectedExpenseTagsByBucketId: cardType === CardType.Physical || this.moreThanOneUserSelected ? {} : selectedExpenseTagsByBucketId,
                textExpenseTagsByBucketId: cardType === CardType.Physical || this.moreThanOneUserSelected ? {} : textExpenseTagsByBucketId,
                cardRequestId,
                cardRequestVersion,
                firstName,
                lastName,
                shippingService,
                shippingRequireSignature,
                cardTemplateId
            }, linkedAccountId, companyId, issuingDetailsOriginalValues, this.cardCapabilities(), (response) => {
                // only push event when we first issue card not on update
                if (!this.isInEditMode()) {
                    PendoService.trackEvent(PendoEvents.CARD_ISSUED, {
                        issued_to: assigneeUserId
                    });
                }
                onSubmit(response);
            });
        };
        let stateParams = {
            // Values
            allowanceInterval: AccountCapabilitiesCardAllowanceIntervalEnum.Continuous.toLowerCase(),
            allowanceMerchantCategories: [],
            allowanceMerchantGroupCategories: [],
            allowanceVendor: '',
            cardType: CardsGatewayIssuingModal.getDefaultCardType(props),
            selectedExpenseTagsByBucketId: {},
            textExpenseTagsByBucketId: {},
            cardRequestId: (_a = props.cardRequest) === null || _a === void 0 ? void 0 : _a.id,
            cardRequestVersion: (_b = props.cardRequest) === null || _b === void 0 ? void 0 : _b.version,
            shippingOptions: {
                Standard: [],
                Express: [],
                Priority: []
            },
            shippingRequireSignature: false
        };
        if (props.selectedCreateCardType) {
            if (props.selectedCreateCardType === CardType.Physical) {
                stateParams.cardType = CardType.Physical;
            }
        }
        stateParams.enableMultiSelect = isMultiSelectAllowed(stateParams, props);
        const cardTemplate = props.cardTemplate;
        if (props.issuingMode === 'template' && cardTemplate) {
            const isVirtualCardForm = CardsGatewayIssuingModal.shouldBeVirtualCardForm(props.capabilities);
            stateParams.cardTemplateId = cardTemplate.id;
            stateParams.cardName = cardTemplate.purpose;
            stateParams.allowanceAmount = cardTemplate.budget;
            stateParams.allowanceInterval = cardTemplate.frequency;
            stateParams.allowanceStartDate = cardTemplate.start_date ? moment(cardTemplate.start_date) : null;
            stateParams.allowanceEndDate = cardTemplate.end_date ? moment(cardTemplate.end_date) : null;
            const merchantCategoryCodes = cardTemplate.category_codes ? cardTemplate.category_codes.split(',') : [];
            const cardCapabilities = isVirtualCardForm ? props.capabilities.virtualCard : props.capabilities.card;
            if (merchantCategoryCodes.length <= Number((_c = cardCapabilities === null || cardCapabilities === void 0 ? void 0 : cardCapabilities.allowance) === null || _c === void 0 ? void 0 : _c.supportedMerchantGroupRestrictions)) {
                stateParams.allowanceMerchantGroupCategories = merchantCategoryCodes;
            }
        }
        this.state = stateParams;
        this.isManager = isManagerFlagCheck(this.props.userProfile);
    }
    getIsCardIssuingSupported() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            let canIssueCard = false;
            if (((_a = this.props.userProfile) === null || _a === void 0 ? void 0 : _a.role) === ADMIN_ROLE) {
                return true;
            }
            if (((_b = this.props.userProfile) === null || _b === void 0 ? void 0 : _b.role) === MANAGER_ROLE) {
                const company = window.ORG;
                const canManagersIssueCards = company.is_manager_card_issuing_enabled;
                if (!canManagersIssueCards) {
                    return false;
                }
                // if managers can issue cards and this isn't preloading a card request, allow managers to issue card
                if (!this.state.cardRequestId) {
                    return true;
                }
                const cardRequestJourney = yield CardRequestAPI.getJourney(this.state.cardRequestId);
                const hasPendingApproverSteps = cardRequestJourney.some((step) => step.type === 'manager_approval' && step.time_period === 'future');
                canIssueCard = !hasPendingApproverSteps;
                return canIssueCard;
            }
            return false;
        });
    }
    static getSelectedExpenseTagsFromInitialValue(activeExpenseBucketsWithActiveTags, expense_tags_to_linked_account) {
        if (expense_tags_to_linked_account) {
            const selectedExpenseTagsByBucketId = {};
            const textExpenseTagsByBucketId = {};
            expense_tags_to_linked_account.forEach((etlc) => {
                const bucket = activeExpenseBucketsWithActiveTags.find((etb) => etb.id === etlc.expense_tag_bucket_id);
                if ((bucket === null || bucket === void 0 ? void 0 : bucket.input_type) === 'text') {
                    const tag = bucket.expense_tags.find((et) => et.id === etlc.expense_tag_id);
                    textExpenseTagsByBucketId[bucket.id] = tag && tag.name;
                }
                else if ((bucket === null || bucket === void 0 ? void 0 : bucket.input_type) === 'dropdown') {
                    selectedExpenseTagsByBucketId[bucket.id] = etlc.expense_tag_id;
                }
            });
            return {
                selectedExpenseTagsByBucketId: selectedExpenseTagsByBucketId,
                textExpenseTagsByBucketId: textExpenseTagsByBucketId
            };
        }
    }
    static getDerivedStateFromProps(props, state) {
        const { issuingDetailsOriginalValues, parentCardOptions, capabilities, cardRequest, userProfile } = props;
        let stateParams = {};
        const isVirtualCardForm = CardsGatewayIssuingModal.shouldBeVirtualCardForm(capabilities);
        if (Boolean(issuingDetailsOriginalValues) && state.id !== issuingDetailsOriginalValues.id) {
            stateParams.id = issuingDetailsOriginalValues.id;
            stateParams.assigneeUserId = issuingDetailsOriginalValues.user_id;
            stateParams.parentCardId = issuingDetailsOriginalValues.parent_card && issuingDetailsOriginalValues.parent_card.id;
            stateParams.cardName = issuingDetailsOriginalValues.description;
            if (isVirtualCardForm) {
                stateParams.cardType = CardType.Virtual;
            }
            else {
                if (cardRequest) {
                    stateParams.cardType = CardsGatewayIssuingModal.getDefaultCardType(props);
                }
                else {
                    stateParams.cardType = issuingDetailsOriginalValues.is_ghost ? CardType.Ghost : CardType.Physical;
                }
            }
            const allowanceObject = issuingDetailsOriginalValues.allowance;
            stateParams.allowanceId = allowanceObject.id;
            stateParams.allowanceAmount = allowanceObject.amount;
            stateParams.allowanceInterval = allowanceObject.interval;
            stateParams.allowanceMerchantCategories = allowanceObject.scope;
            stateParams.allowanceMerchantGroupCategories = allowanceObject.group_scope;
            stateParams.allowanceVendor = allowanceObject.vendor;
            stateParams.allowanceDailyLimit = allowanceObject.daily_limit;
            stateParams.allowanceStartDate = allowanceObject.start_time && moment(allowanceObject.start_time);
            stateParams.allowanceEndDate = allowanceObject.end_time && moment(allowanceObject.end_time);
            if (props.issuingMode === 'duplicate') {
                if (stateParams.allowanceStartDate) {
                    const delta = stateParams.allowanceStartDate.diff(moment(issuingDetailsOriginalValues.created_at), 'days');
                    stateParams.allowanceStartDate = moment().add(delta, 'days');
                }
                if (stateParams.allowanceEndDate) {
                    const delta = stateParams.allowanceEndDate.diff(moment(issuingDetailsOriginalValues.created_at), 'days');
                    stateParams.allowanceEndDate = moment().add(delta, 'days');
                }
            }
            stateParams.allowanceTransactionLimit = allowanceObject.transaction_limit;
            stateParams.selectedCategoryId = issuingDetailsOriginalValues.default_category_id;
            const expenseTags = CardsGatewayIssuingModal.getSelectedExpenseTagsFromInitialValue(props.activeExpenseBucketsWithActiveTags, issuingDetailsOriginalValues.expense_tags_to_linked_account);
            if (expenseTags) {
                stateParams.selectedExpenseTagsByBucketId = expenseTags.selectedExpenseTagsByBucketId;
                stateParams.textExpenseTagsByBucketId = expenseTags.textExpenseTagsByBucketId;
            }
            if (cardRequest) {
                stateParams.addressRecipient = cardRequest.recipient_name;
                stateParams.addressStreet = cardRequest.street_address;
                stateParams.addressCity = cardRequest.address_city;
                stateParams.addressState = cardRequest.address_state;
                stateParams.addressZipCode = cardRequest.postal_code;
            }
        }
        // If there's only 1 parent card option, we'll disable the dropdown, so we should auto-select that 1 option
        if (parentCardOptions.length === 1) {
            stateParams.parentCardId = parentCardOptions[0].id;
        }
        if (cardRequest && !CardsGatewayIssuingModal.canIssueFromCardRequest(cardRequest, userProfile)) {
            stateParams.errorMessage = 'This card has already been issued. Therefore you cannot issue it again.';
        }
        return stateParams;
    }
    componentDidMount() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.props.init(this.isVirtualCardForm(), this.props.parentLinkedAccountId, this.props.linkedAccountId, this.props.cardRequest);
            const canIssueCard = yield this.getIsCardIssuingSupported();
            this._initializeShippingOptions(this.state, (_a = this.props.cardPrograms) === null || _a === void 0 ? void 0 : _a.find(({ id }) => id === this.props.parentLinkedAccountId));
            this.setState({
                canIssueCard: canIssueCard
            });
        });
    }
    // Usually, we prefer showing the form in virtual card mode, then ghost card mode, then physical card mode
    // However, if we're issuing from a physical card request, we want to show the physical card form
    static getDefaultCardType(props) {
        var _a, _b, _c, _d, _e;
        const { capabilities } = props;
        if (((_a = props.cardRequest) === null || _a === void 0 ? void 0 : _a.recipient_name) && ((_b = capabilities.issuing) === null || _b === void 0 ? void 0 : _b.physical)) {
            return CardType.Physical;
        }
        else if ((_c = capabilities.issuing) === null || _c === void 0 ? void 0 : _c.virtual) {
            return CardType.Virtual;
        }
        else if ((_d = capabilities.issuing) === null || _d === void 0 ? void 0 : _d.ghost) {
            return CardType.Ghost;
        }
        else if ((_e = capabilities.issuing) === null || _e === void 0 ? void 0 : _e.physical) {
            return CardType.Physical;
        }
        throw new Error('Cannot issue cards');
    }
    get moreThanOneUserSelected() {
        return this.state.enableMultiSelect && this.state.assigneeUserIds && this.state.assigneeUserIds.length > 1;
    }
    validateFields() {
        var _a, _b, _c, _d, _e, _f, _g;
        const { allowanceAmount, allowanceTransactionLimit, allowanceDailyLimit, allowanceMerchantCategories, allowanceMerchantGroupCategories, allowanceVendor, allowanceEndDate, cardName, assigneeUserIds, assigneeUserId, assigneeEmail, cardType, addressRecipient, addressStreet, addressCity, addressState, addressZipCode, addressPhoneNumber, parentCardId, firstName, lastName } = this.state;
        const capabilities = this.cardCapabilities();
        const program = (_a = this.props.cardPrograms) === null || _a === void 0 ? void 0 : _a.find(({ id }) => id === this.props.parentLinkedAccountId);
        const isNewUser = !assigneeUserId && assigneeEmail;
        const isInvitedUser = assigneeUserId && ((_b = this.props.usersById[assigneeUserId]) === null || _b === void 0 ? void 0 : _b.is_invited);
        const firstAndLastNameRequired = window.FEATURES.consolidated_invite_issuing && (isNewUser || isInvitedUser) && cardType === CardType.Physical;
        if (this.state.enableMultiSelect) {
            if (!assigneeUserIds || assigneeUserIds.length === 0) {
                return 'Employee is required';
            }
        }
        else {
            if (!assigneeUserId && (!assigneeEmail || !isEmail(assigneeEmail))) {
                return 'Employee is required';
            }
        }
        if (assigneeUserId && this.props.usersById[assigneeUserId] && !this.props.usersById[assigneeUserId].phone_number) {
            if ((program === null || program === void 0 ? void 0 : program.institution_type) === AccountConnectionFeedBackendEnum.AmexCpg) {
                this.setState({ needsUserUpdate: true });
                return "Employee doesn't have a phone number - card cannot be issued";
            }
        }
        if (!cardName && !firstAndLastNameRequired) {
            return `Card ${this.isVirtualCardForm() ? 'Purpose' : 'Name'} is required`;
        }
        if (!firstName && firstAndLastNameRequired) {
            return 'First Name is required';
        }
        if (!lastName && firstAndLastNameRequired) {
            return 'Last Name is required';
        }
        if (!allowanceAmount || allowanceAmount <= 0) {
            return CARD_ISSUING_ERRORS.BUDGET_MUST_BE_POSITIVE;
        }
        if (typeof allowanceDailyLimit === 'number' && allowanceDailyLimit <= 0) {
            return 'Daily Budget must be greater than $0.00';
        }
        if (typeof allowanceDailyLimit === 'number' && allowanceDailyLimit > allowanceAmount) {
            return 'Daily Budget cannot exceed Total Budget';
        }
        if (typeof allowanceTransactionLimit === 'number' &&
            typeof allowanceDailyLimit === 'number' &&
            allowanceTransactionLimit > allowanceDailyLimit) {
            return 'Transaction Limit cannot exceed Daily Limit';
        }
        if (typeof allowanceTransactionLimit === 'number' && allowanceTransactionLimit > allowanceAmount) {
            return 'Transaction Limit cannot exceed Total Budget';
        }
        if (allowanceVendor && allowanceVendor.length > 0 && ((_c = capabilities === null || capabilities === void 0 ? void 0 : capabilities.allowance) === null || _c === void 0 ? void 0 : _c.allowedVendor) === false) {
            return 'Vendor Restrictions are not allowed for this card program.';
        }
        if ((allowanceMerchantCategories === null || allowanceMerchantCategories === void 0 ? void 0 : allowanceMerchantCategories.length) > ((_d = capabilities === null || capabilities === void 0 ? void 0 : capabilities.allowance) === null || _d === void 0 ? void 0 : _d.supportedMerchantRestrictions)) {
            return `A maximum of ${(_e = capabilities === null || capabilities === void 0 ? void 0 : capabilities.allowance) === null || _e === void 0 ? void 0 : _e.supportedMerchantRestrictions} Merchant Category Codes can be selected.`;
        }
        if ((allowanceMerchantGroupCategories === null || allowanceMerchantGroupCategories === void 0 ? void 0 : allowanceMerchantGroupCategories.length) > ((_f = capabilities === null || capabilities === void 0 ? void 0 : capabilities.allowance) === null || _f === void 0 ? void 0 : _f.supportedMerchantGroupRestrictions)) {
            return `A maximum of ${(_g = capabilities === null || capabilities === void 0 ? void 0 : capabilities.allowance) === null || _g === void 0 ? void 0 : _g.supportedMerchantGroupRestrictions} Merchant Group Category Codes can be selected.`;
        }
        if (allowanceEndDate === null || allowanceEndDate === void 0 ? void 0 : allowanceEndDate.isSameOrBefore(new Date(), 'day')) {
            return 'The date cannot be in the past.';
        }
        const addressIsIncomplete = !addressRecipient || !addressStreet || !addressCity || !addressState || !addressZipCode;
        if (!this.isInEditMode() && cardType === CardType.Physical && addressIsIncomplete) {
            return 'Shipping Address is required for Plastic Cards';
        }
        if ((program === null || program === void 0 ? void 0 : program.institution_type) === INSTITUTION_TYPE.EMBURSE && addressStreet && addressStreet.length > 50) {
            return 'Street Address must be 50 characters or less.';
        }
        if (cardType === CardType.Virtual && !parentCardId) {
            return 'Parent Card is required for Virtual Cards.';
        }
        if (this.isManager && !assigneeUserId) {
            return 'Managers Cannot Invite Users.';
        }
        if (addressPhoneNumber && addressPhoneNumber.length !== 10) {
            return 'Phone number must be 10 digits.';
        }
        return null;
    }
    getFieldsForForwardingRequest() {
        const seleectedExpenseTagIds = Object.values(this.state.selectedExpenseTagsByBucketId);
        const textExpenseTagsByBucketId = this.state.textExpenseTagsByBucketId;
        return Object.assign(Object.assign({}, this.props.cardRequest), { purpose: this.state.cardName || '', budget: this.state.allowanceAmount || 0, frequency: this.state.allowanceInterval /** this needs a type */, default_category_id: this.state.selectedCategoryId /** double check the ICardRequestModel, lot of these props are optional */, expense_tag_ids: seleectedExpenseTagIds, text_expense_tags: textExpenseTagsByBucketId });
    }
    tryParseAndReturnFormattedAMEXError(serverError) {
        try {
            const errors = getAmexErrorsFromAmexGatewayValidationError(serverError);
            if (!(errors === null || errors === void 0 ? void 0 : errors.length)) {
                return null;
            }
            const cardErrors = errors.map((err, index) => {
                return (_jsxs("div", { children: [_jsx(Typography, Object.assign({ className: "underlineText" }, { children: "Error Details:" })), _jsxs(Typography, { children: ["Error code: ", err.code] }), _jsxs(Typography, { children: ["Error message: ", err.detail] })] }, index));
            });
            return (_jsxs("div", { children: [_jsx(Typography, { children: "This card cannot be issued at the moment - there was a problem during the verification of the card holder information." }), _jsx(Spacer, { size: 5 }), cardErrors] }));
        }
        catch (err) {
            // ignore errors - it was not AMEX Json error
        }
    }
    renderServerError(error) {
        const errors = parseCardsGatewayError(error);
        if (errors.length === 0) {
            return;
        }
        const cardErrors = errors.map((err, index) => {
            return (_jsx("li", { children: _jsx(Typography, { children: err }) }, `${err}-${index}`));
        });
        return (_jsxs("div", { children: [_jsx(Typography, { children: "The card could not be created (or updated) due to the following reason(s):" }), _jsx("ul", Object.assign({ style: { paddingLeft: '20px' } }, { children: cardErrors }))] }));
    }
    getOnlyOneUserInMultiSelect() {
        var _a;
        if (this.state.enableMultiSelect && ((_a = this.state.assigneeUserIds) === null || _a === void 0 ? void 0 : _a.length) === 1) {
            return this.state.assigneeUserIds[0];
        }
        return undefined;
    }
    getAssignedUserId() {
        const userIdOrEmail = this.getOnlyOneUserInMultiSelect();
        if (userIdOrEmail) {
            if (userIdOrEmail.indexOf('@') > -1) {
                return undefined;
            }
            return userIdOrEmail;
        }
        return this.state.assigneeUserId;
    }
    getAssignedUserEmail() {
        const userIdOrEmail = this.getOnlyOneUserInMultiSelect();
        if (userIdOrEmail) {
            if (userIdOrEmail.indexOf('@') > -1) {
                return userIdOrEmail;
            }
            return undefined;
        }
        return this.state.assigneeEmail;
    }
    renderCardFormFields() {
        const { capabilities, linkedAccountId, institutionName, userOptions, usersById, parentCardOptions, parentCardsById, activeExpenseBucketsWithActiveTags, cardRequest, loading, showAsDrawer, selectedCreateCardType } = this.props;
        const allowCardTypeSelector = !selectedCreateCardType || !this.state.cardType;
        return (_jsxs(_Fragment, { children: [allowCardTypeSelector && (_jsx(CardTypeForm, { linkedAccountId: linkedAccountId, capabilities: capabilities, cardType: this.state.cardType, onChange: this.onChange, userProfile: this.props.userProfile })), _jsx(IssuingDetailsForm, { cardRequestId: cardRequest === null || cardRequest === void 0 ? void 0 : cardRequest.id, capabilities: capabilities, linkedAccountId: linkedAccountId, institutionName: institutionName, userOptions: userOptions, usersById: usersById, parentCardOptions: parentCardOptions, parentCardsById: parentCardsById, values: Object.assign({}, this.state), loading: loading, isInEditMode: this.isInEditMode(), onChange: this.onChange, showAsDrawer: showAsDrawer, userProfile: this.props.userProfile, multiple: this.state.enableMultiSelect || false }), this.state.cardType !== CardType.Physical && (_jsxs(_Fragment, { children: [_jsx(Spacer, { size: 16 }), _jsx(ExpenseFieldsForm, { cardsIssuingExpenseFieldsFeatureFlag: window.FEATURES.emburse_cards_gateway_issuing_expense_fields, assigneeUserId: this.getAssignedUserId(), assigneeEmail: this.getAssignedUserEmail(), selectedCategoryId: this.moreThanOneUserSelected ? null : this.state.selectedCategoryId, selectedExpenseTagsByBucketId: this.moreThanOneUserSelected ? {} : this.state.selectedExpenseTagsByBucketId, textExpenseTagsByBucketId: this.state.textExpenseTagsByBucketId, activeExpenseBucketsWithActiveTags: activeExpenseBucketsWithActiveTags, onChange: this.onChange, disabled: this.moreThanOneUserSelected })] })), this.state.cardType === CardType.Physical && !this.isInEditMode() && (_jsxs(_Fragment, { children: [_jsx(Spacer, { size: 16 }), _jsx(AddressForm, { recipient: this.state.addressRecipient, street: this.state.addressStreet, city: this.state.addressCity, state: this.state.addressState, zipCode: this.state.addressZipCode, phoneNumber: this.state.addressPhoneNumber, includePhoneNumberField: this.state.isEligibleForExpeditedShipping, onChange: this.onChange }), this.state.showShippingMethodForm && this.state.shippingService && (_jsxs(_Fragment, { children: [_jsx(Spacer, { size: 16 }), _jsx(ShippingMethodForm, { service: this.state.shippingService, requireSignature: this.state.shippingRequireSignature, shippingMethods: this.state.shippingOptions, onChange: this.onChange })] }))] }))] }));
    }
    renderModalBody() {
        const { loading, serverError, showAsDrawer } = this.props;
        const { errorMessage } = this.state;
        const isLoadingBody = loading === 'body';
        let errToRender = errorMessage || this.renderServerError(serverError);
        if (!errorMessage && serverError) {
            errToRender = this.tryParseAndReturnFormattedAMEXError(serverError) || errToRender;
        }
        return (_jsxs("div", Object.assign({ className: classNames(style.global, 'card-issuing-modal'), style: { margin: '8px 0' } }, { children: [isLoadingBody && (_jsx("div", Object.assign({ className: "flex ai-c jc-c", style: { marginTop: showAsDrawer ? '150px' : 0 }, "data-testid": "Spinner" }, { children: _jsx(CircularProgress, {}) }))), _jsxs("div", Object.assign({ className: classNames({ 'card-issuing-drawer': showAsDrawer, hidden: isLoadingBody }), "data-testid": "Card-Form-Fields-Container", hidden: isLoadingBody }, { children: [errToRender && _jsx(Alert, Object.assign({ severity: "error" }, { children: errToRender })), !isLoadingBody && this.renderCardFormFields()] }))] })));
    }
    renderApproveButton() {
        // this should target the updateCardStatus flow and pass in the correct payload
        if (this.state.canIssueCard) {
            return (_jsx(Button, Object.assign({ disabled: !this.props.cardRequest, variant: "outlined", onClick: this.approveCardRequest }, { children: "Approve and Forward" })));
        }
        return (_jsx(Button, Object.assign({ disabled: !this.props.cardRequest, primary: true, onClick: this.approveCardRequest }, { children: "Approve and Forward" })));
    }
    render() {
        var _a;
        const company = window.ORG;
        const cardRequest = this.props.cardRequest;
        const role = this.props.userProfile.role;
        const canIssueCard = this.state.canIssueCard;
        const showDenyButton = role === MANAGER_ROLE && (cardRequest === null || cardRequest === void 0 ? void 0 : cardRequest.status) === 'pending_manager_review';
        let cardIssuingWarning = ``;
        const managerIssueCardError = canManagerIssueRequestedCard(company, this.props.userProfile, this.state.cardType, this.state.allowanceAmount || ((_a = this.props.cardRequest) === null || _a === void 0 ? void 0 : _a.budget));
        if (this.props.userProfile.role === MANAGER_ROLE) {
            cardIssuingWarning = managerIssueCardError;
        }
        else {
            if (this.props.loading) {
                cardIssuingWarning = 'Card issuing in progress';
            }
            else {
                cardIssuingWarning = "You can't issue this card request";
            }
        }
        const disableCardIssuingButton = this.props.loading ||
            (this.props.cardRequest && !CardsGatewayIssuingModal.canIssueFromCardRequest(this.props.cardRequest, this.props.userProfile)) ||
            (this.props.userProfile.role === MANAGER_ROLE && managerIssueCardError) ||
            this.state.needsUserUpdate;
        const showApproveButton = Boolean(window.ORG.is_manager_card_issuing_enabled && this.props.userProfile.role === 'manager' && this.props.onApprove);
        if (this.props.showAsDrawer) {
            return (_jsxs(_Fragment, { children: [this.renderModalBody(), _jsx("div", { style: { marginTop: 'auto' } }), _jsxs(ButtonContainer, Object.assign({ alignButtons: "right", className: "drawer-buttons" }, { children: [showDenyButton && (_jsx(Button, Object.assign({ variant: "outlined", onClick: this.onDeny, color: "error" }, { children: "Deny" }))), !showDenyButton && (_jsx(Button, Object.assign({ variant: "outlined", onClick: this.onClose }, { children: "Cancel" }))), showApproveButton ? this.renderApproveButton() : null, canIssueCard && (_jsx(Tooltip, Object.assign({ title: disableCardIssuingButton ? cardIssuingWarning : '' }, { children: _jsx(Button, Object.assign({ disabled: disableCardIssuingButton, primary: true, onClick: this.submitCard }, { children: this.isInEditMode() ? 'Update' : 'Issue Card' })) })))] }))] }));
        }
        return (_jsx(Modal, Object.assign({ open: true, modalHeader: this.isInEditMode() ? 'Update' : 'Issue New Card', titleonClose: this.onClose, modalFooter: _jsx(ModalFooter, { children: _jsxs(ButtonContainer, Object.assign({ alignButtons: "right" }, { children: [showDenyButton && (_jsx(Button, Object.assign({ variant: "outlined", onClick: this.onDeny, color: "error" }, { children: "Deny" }))), !showDenyButton && (_jsx(Button, Object.assign({ variant: "outlined", onClick: this.onClose }, { children: "Cancel" }))), this.renderApproveButton(), canIssueCard && (_jsx(Tooltip, Object.assign({ title: disableCardIssuingButton ? cardIssuingWarning : '' }, { children: _jsx(Button, Object.assign({ disabled: disableCardIssuingButton, primary: true, onClick: this.submitCard }, { children: this.isInEditMode() ? 'Update' : 'Issue Card' })) })))] })) }) }, { children: this.renderModalBody() })));
    }
}
CardsGatewayIssuingModal.shouldBeVirtualCardForm = (capabilities) => { var _a; return ((_a = capabilities === null || capabilities === void 0 ? void 0 : capabilities.issuing) === null || _a === void 0 ? void 0 : _a.virtual) || false; };
CardsGatewayIssuingModal.canIssueFromCardRequest = (cardRequest, userProfile) => {
    const isAssignedManager = cardRequest.manager_id === userProfile.id;
    const isAdmin = userProfile.role === ADMIN_ROLE;
    return (['new', 'pending_final_approval', 'pending_invite'].includes(cardRequest.status) ||
        // Admin that is also the manager should be able to issue card without approve step
        (cardRequest.status === 'pending_manager_review' && (isAssignedManager || isAdmin)));
};
const selectActiveUsers = createSelector((state) => state.users.usersActiveOrInvited, (usersActiveOrInvited) => usersActiveOrInvited.filter((user) => user.user_profile.state === 'active'));
const selectManagedUsers = createSelector([(state) => state.users.users, (state) => state.currentUser.userProfile], (users, userProfile) => users.filter((user) => user.user_profile.manager_user_profile_id === userProfile.id));
const getUserOptions = (state) => {
    const userProfile = state.currentUser.userProfile;
    if (userProfile.role === MANAGER_ROLE) {
        return selectManagedUsers(state);
    }
    else if (window.FEATURES.accelerated_card_issuing) {
        return state.users.usersActiveOrInvited;
    }
    return selectActiveUsers(state);
};
const mapStateToProps = (state, ownProps) => (Object.assign(Object.assign(Object.assign({ userOptions: getUserOptions(state) }, ownProps), state.cardsGatewayIssuingModal), { usersById: state.users.usersById, activeExpenseBucketsWithActiveTags: state.expenseBucketsWithTags.activeExpenseBucketsWithActiveTags, userProfile: state.currentUser.userProfile, cardPrograms: state.cards.programs }));
const mapDispatchToProps = {
    init,
    submit,
    reset
};
const connector = connect(mapStateToProps, mapDispatchToProps);
export const CardsGatewayIssuingModalConnected = connector(CardsGatewayIssuingModal);
